import * as React from "react";
import { logger } from "bernie-core";
import { inject, observer } from "mobx-react";
import { useQuery } from "@apollo/client";

import { getComponentFromView } from "./utils/WizardHotelPWAv2Utils";
import { WizardHotelPWAv2FlexModuleModelResult, WizardHotelPWAv2Props } from "./typings";
import {
  TravelAgentSearchWizardConfigQueryDocument,
  TravelAgentSearchWizardConfigQueryQueryVariables,
} from "__generated__/typedefs";
import { Experiment, isVariantEnabled } from "components/utility/experiment";
import { getOffsetDatesByDatesOption } from "src/components/utility/OffsetDate/OffsetDateUtil";
import { SystemEvent, SystemEventLevel } from "@shared-ui/logger-context";
import { useInitializer } from "src/utils/hooks/useInitializer";
import { travelersState } from "src/stores/wizard/state/global/travelers";
import { LastSearch } from "./utils/LastSearchTypes";
import { useBexApi } from "@shared-ui/bex-api-context";

const WizardHotelPWAv2Module = (props: WizardHotelPWAv2Props) => {
  const { context, templateComponent, flexModuleModelStore, flexViewModel, analytics, wizardState } = props;
  const { hotelWizardState } = wizardState;
  const {
    metadata: { id },
  } = templateComponent;

  const options = flexModuleModelStore.getModel(id) as WizardHotelPWAv2FlexModuleModelResult;
  const { context: bexApiContext } = useBexApi();
  const travelAgentQueryVariables: TravelAgentSearchWizardConfigQueryQueryVariables = {
    context: bexApiContext,
  };

  const {
    config: { view = "" },
  } = templateComponent;

  const isLastSearchActive = isVariantEnabled(context, Experiment.BEX_Last_Search_Prefill_Lodging);

  const updateFieldsFromConfigAndContext = () => {
    hotelWizardState.updateDateFromConfig();
    if (options.destinationFromContext) {
      hotelWizardState.updateDestinationFromContext(context);
    }
  };

  useInitializer(() => {
    const hotelWizardState = props.wizardState.hotelWizardState;
    const hotelWizardConfig = hotelWizardState.config;
    const { essAdapterConfig } = hotelWizardConfig.location.destination;
    const isDesktop = Boolean(!context.deviceInformation?.mobile && !context.deviceInformation?.tablet);

    hotelWizardState.overrideConfig(() => {
      hotelWizardConfig.elementId = id;
      hotelWizardConfig.heading.fixedTitle = options.fixedLobTitle !== false;
      hotelWizardConfig.heading.title = options.lobTitle;
      hotelWizardConfig.includeFiltersHotelPWAWizard = options.includeFiltersHotelPWAWizard;
      hotelWizardConfig.analytics = analytics;
      hotelWizardConfig.enableMultiplePlaces = options.enableMultiplePlaces;

      if (!hotelWizardConfig.useTravelAgentSearchWizardConfig) {
        hotelWizardConfig.heading.hideLobTitle = options.hideLobTitle;
      }

      hotelWizardConfig.heading.lobSubtitleBreakPoint = options.lobSubtitleBreakPoint;
      hotelWizardConfig.siteIdsWithoutSoftPackages = options.siteIdsWithoutSoftPackages || [];
      hotelWizardConfig.desktopAutoOpenCalendar = Boolean(isDesktop && options.desktopAutoOpenCalendar);

      hotelWizardConfig.enablePackageRates = options.enablePackageRates ?? hotelWizardConfig.enablePackageRates;
      hotelWizardConfig.percentSave = options.percentSave ?? hotelWizardConfig.percentSave;
      hotelWizardConfig.packageRatesTermsUrl = options.packageRatesTermsUrl ?? hotelWizardConfig.packageRatesTermsUrl;
      hotelWizardConfig.showDestinationField = options.showDestinationField ?? hotelWizardConfig.showDestinationField;
      hotelWizardConfig.useTravelAgentSearchWizardConfig =
        options.useTravelAgentSearchWizardConfig ?? hotelWizardConfig.useTravelAgentSearchWizardConfig;

      const currentSiteId = props.context?.site?.id.toString();

      if (
        hotelWizardConfig.siteIdsWithoutSoftPackages.length !== 0 &&
        hotelWizardConfig.siteIdsWithoutSoftPackages.includes(currentSiteId)
      ) {
        hotelWizardConfig.enableAddFlight = false;
        hotelWizardConfig.enableAddCar = false;
      } else {
        hotelWizardConfig.enableAddFlight = options.enableAddFlight;
        hotelWizardConfig.enableAddCar = options.enableAddCar;
      }

      if (flexViewModel && flexViewModel.userLocation) {
        essAdapterConfig.latLong = `${flexViewModel.userLocation.latitude},${flexViewModel.userLocation.longitude}`;
      }

      essAdapterConfig.trending = true;

      essAdapterConfig.alternate = isVariantEnabled(context, Experiment.BEX_Typeahead_Alt_Destination_Web);

      essAdapterConfig.historyDetail =
        essAdapterConfig.lob === "HOTELS" &&
        isVariantEnabled(context, Experiment.BEX_WEB_Typeahead_History_With_details);

      const { defaultOffsetDates } = hotelWizardConfig.date;
      const { startDateOffset, endDateOffset } = getOffsetDatesByDatesOption(options);

      if (defaultOffsetDates && startDateOffset !== undefined && endDateOffset !== undefined) {
        defaultOffsetDates.start = startDateOffset;
        defaultOffsetDates.end = endDateOffset;
      }
    });

    hotelWizardConfig.travelerType = options.defaultTravelerType;

    if (!isLastSearchActive) {
      updateFieldsFromConfigAndContext();
    }

    const updateFieldsFromLastSearch = (lastSearchData: LastSearch[]) => {
      if (lastSearchData.length === 1) {
        const [lastSearchResult] = lastSearchData;
        const { regionNames, searchDetail } = lastSearchResult;
        const { maxBookableTravelers } = hotelWizardState.config.travelers;

        /**
         * Last search only applies when there's exactly one room.
         * The service is returning adullt/child value null when there's more than one room
         * If last search travelers cant't be provided we avoid entirely last search.
         */
        const hasLastSearch =
          searchDetail &&
          searchDetail.adultTravelerCounts &&
          searchDetail.childTravelerCounts &&
          searchDetail.totalTravelerCount !== null;

        if (hasLastSearch && maxBookableTravelers > searchDetail.totalTravelerCount) {
          hotelWizardState.updateDestinationSelection({ term: regionNames.lastSearchName, data: lastSearchResult });

          const startDate = hotelWizardState.getDateTimezoneOffsetFromDatestamp(searchDetail.startDate);
          const endDate = hotelWizardState.getDateTimezoneOffsetFromDatestamp(searchDetail.endDate);

          hotelWizardState.updateDateSelection(startDate, endDate);

          const lastSearchTravelers = { ...travelersState.hotel };

          const lastSearchRooms = hotelWizardState.formatTravelersFromHistoryDetail(searchDetail);
          const updatedLastSearchTravelers = {
            ...lastSearchTravelers,
            rooms: lastSearchRooms,
          };

          hotelWizardState.updateHotelTravelersSelection(updatedLastSearchTravelers);
        } else {
          updateFieldsFromConfigAndContext();
        }
      } else {
        updateFieldsFromConfigAndContext();
      }
    };

    if (isLastSearchActive && !hotelWizardState.hasCalledLastSearch) {
      hotelWizardState
        .getLastSearchFromAPI({
          expuserid: context.user.expUserId,
          guid: context.guid,
          locale: context.locale,
          siteid: context.site.id,
          regiontype: "2047",
        })
        .then((response) => response.json())
        .then((data) => {
          updateFieldsFromLastSearch(data.sr);
          hotelWizardState.updateHasLastSearch(true);
        })
        .catch(() => {
          updateFieldsFromConfigAndContext();
        });
    }

    if (options.titleFromPageHeader) {
      hotelWizardConfig.heading.differentiatedTitles = false;
    }

    if (options.subTitleFromPageSubHeader) {
      hotelWizardConfig.heading.lobSubtitleBreakPoint = "on";
    }
  });

  const { data, error, loading } = useQuery(TravelAgentSearchWizardConfigQueryDocument, {
    variables: travelAgentQueryVariables,
    skip: hotelWizardState.config.useTravelAgentSearchWizardConfig ? false : true,
    ssr: false,
  });

  if (error) {
    props.logger.logEvent(
      new SystemEvent(SystemEventLevel.ERROR, "FAILED_TRAVEL_AGENT_FETCH"),
      "Failed to fetch travel agent configuration",
      error.message
    );
  }

  if (!loading && data) {
    hotelWizardState.updateFromTravelAgentConfig(data.travelAgentSearchWizardConfig);
  }

  const ViewComponent = getComponentFromView(view);

  return <ViewComponent {...props} />;
};

export const WizardHotelPWAv2 = logger("WizardHotelPWAv2")(
  inject(
    "analytics",
    "experiment",
    "page",
    "wizardState",
    "compositionStore",
    "flexModuleModelStore",
    "flexViewModel"
  )(observer(WizardHotelPWAv2Module))
);

export default WizardHotelPWAv2;
